import { useState } from "react";
import styled from "styled-components";
import {
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  Stack,
  Typography,
} from "@mui/material";

import ArrowBackIosOutlinedIcon from "@mui/icons-material/ArrowBackIosOutlined";
import CreditCardIcon from "@mui/icons-material/CreditCard";
// import QrCodeIcon from "@mui/icons-material/QrCode";
// import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import DomainIcon from "@mui/icons-material/Domain";
import ArticleIcon from "@mui/icons-material/Article";
import PaymentIcon from "@mui/icons-material/Payment";

import font from "src/styles/font";
import device from "src/styles/device";
import {
  Black,
  DarkGrey,
  Grey,
  GreyStroke,
  Primary,
  White,
  White01,
} from "src/styles/color";
import { checkNotEmpty, convertIntToCurrency } from "src/common";
import { useDialog } from "src/context/DialogContext";

import Button from "src/components/core/Button";
import TextField from "src/components/core/TextField";
import TermsPolicyDialog from "./TermsPolicyDialog";

const Container = styled.div`
  width: 100%;
  max-width: 1240px;
  margin: 0 auto;
  padding: 24px;
`;

const Wrapper = styled(Box)`
  width: 100%;
  border-radius: 16px;
  border: 1px solid ${White01};
  background-color: ${White};
  box-shadow:
    0 1px 3px 0 rgba(0, 0, 0, 0.1),
    0 1px 2px -1px rgba(0, 0, 0, 0.1);
`;

const WrapperInfo = styled(Wrapper)`
  padding: 24px;

  &:not(:last-child) {
    margin-bottom: 24px;
  }
`;

const SpanTitle = styled.div`
  color: ${({ color }) => (color ? color : Black)};
  font-size: ${font.size.xl};
  font-weight: 700;
  margin-bottom: ${({ mb }) => (mb ? `${mb}px` : 0)};

  @media ${device.tabletL} {
    font-size: ${font.size.lg};
  }
`;

const SpanSubTitle = styled.div`
  color: ${({ color }) => (color ? color : Grey)};
  font-size: ${font.size.sm};
  font-weight: ${({ fontWeight }) => (fontWeight ? fontWeight : 400)};
  margin-bottom: ${({ mb }) => (mb ? `${mb}px` : 0)};

  span {
    font-size: ${font.size.sm};
    font-weight: 400;
  }

  @media ${device.tabletL} {
    font-size: ${font.size.xs};
  }
`;

const SpanItem = styled.div`
  color: ${({ color }) => (color ? color : DarkGrey)};
  font-size: ${font.size.md};
  font-weight: ${({ fontWeight }) => (fontWeight ? fontWeight : 400)};
  margin-bottom: ${({ mb }) => (mb ? `${mb}px` : 0)};

  span {
    font-size: ${font.size.md};
    font-weight: ${({ fontWeight }) => (fontWeight ? fontWeight + 200 : 600)};
  }

  @media ${device.tabletL} {
    font-size: ${font.size.sm};
  }
`;

const PaymentMethod = styled.div`
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(3, 1fr);
`;

const PaymentMethodItem = styled.div`
  flex: 1;
  padding: 16px;
  background-color: ${White};
  border: 1px solid ${GreyStroke};
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.2s ease-in-out;

  &:hover {
    border-color: ${Primary};
  }
  &.active {
    border-color: ${Primary};
    color: ${Primary};
    box-shadow:
      0 1px 3px 0 rgba(0, 0, 0, 0.1),
      0 1px 2px -1px rgba(0, 0, 0, 0.1);

    .MuiSvgIcon-root,
    ${SpanItem} {
      color: ${Primary};
    }
  }
`;

const WrapperTextField = styled.div`
  width: 100%;

  &:not(:last-child) {
    margin-bottom: 24px;
  }
  &:not(:last-child) .MuiFormHelperText-root {
    position: absolute;
    bottom: -20px;
  }
`;

const AcceptTermsWrapper = styled.div`
  width: 100%;
  padding: 16px;
  border: 1px solid ${GreyStroke};
  background-color: #f9fafb;
  border-radius: 16px;
`;

const PaymentSection = ({
  formData,
  setFormData,
  handleBack,
  handleSubmit,
}) => {
  const { openDialog, closeDialog } = useDialog();

  const [isAcceptTerms, setIsAcceptTerms] = useState(false);

  const isValid = [
    "payment_method",
    "billing.name",
    "billing.address",
    "billing.tax_id",
  ].every((field) => {
    const keys = field.split(".");
    const value = keys.reduce(
      (obj, key) => (obj ? obj[key] : undefined),
      formData,
    );
    if (field === "billing.tax_id" && !formData.billing?.is_company) {
      return true; // If not a company, tax_id is not required
    } else {
      if (field === "billing.tax_id") {
        return checkNotEmpty(value) && value.length <= 13; // For company, tax_id must be filled and not exceed 13 characters
      }
      return checkNotEmpty(value);
    }
  });

  return (
    <Container>
      <Grid container rowSpacing={2} columnSpacing={4}>
        <Grid item xs={12}>
          <Button
            variant="textdark"
            startIcon={<ArrowBackIosOutlinedIcon />}
            onClick={handleBack}
          >
            กลับไปหน้าแพ็คเกจ
          </Button>
        </Grid>

        <Grid item xs={12} md={7.5}>
          {/* Payment Information */}
          <WrapperInfo>
            <SpanTitle mb={16}>ข้อมูลการชำระเงิน</SpanTitle>

            <PaymentMethod>
              {/* <PaymentMethodItem
                className={
                  formData.payment_method === "promptpay" ? "active" : ""
                }
                onClick={() =>
                  setFormData((prev) => ({
                    ...prev,
                    payment_method: "promptpay",
                  }))
                }
              >
                <QrCodeIcon sx={{ fontSize: 32, color: DarkGrey }} />
                <SpanItem>PromptPay</SpanItem>
              </PaymentMethodItem> */}

              <PaymentMethodItem
                className={
                  formData.payment_method === "credit_card" ? "active" : ""
                }
                onClick={() =>
                  setFormData((prev) => ({
                    ...prev,
                    payment_method: "credit_card",
                  }))
                }
              >
                <CreditCardIcon sx={{ fontSize: 32, color: DarkGrey }} />
                <SpanItem>บัตรเครดิต</SpanItem>
              </PaymentMethodItem>

              {/* <PaymentMethodItem
                className={
                  formData.payment_method === "bank_transfer" ? "active" : ""
                }
                onClick={() =>
                  setFormData((prev) => ({
                    ...prev,
                    payment_method: "bank_transfer",
                  }))
                }
              >
                <AccountBalanceIcon sx={{ fontSize: 32, color: DarkGrey }} />
                <SpanItem>Bank Transfer</SpanItem>
              </PaymentMethodItem> */}
            </PaymentMethod>
          </WrapperInfo>

          {/* Billing Address */}
          <WrapperInfo>
            <SpanTitle mb={16}>ที่อยู่สําหรับเรียกเก็บเงิน</SpanTitle>

            <WrapperTextField>
              <TextField
                fullWidth
                label="ชื่อ/ชื่อบริษัท"
                value={formData.billing?.name || ""}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    billing: { ...prev.billing, name: e.target.value },
                  }))
                }
                error={!checkNotEmpty(formData.billing?.name)}
                helperText={
                  !checkNotEmpty(formData.billing?.name) &&
                  "กรุณากรอกชื่อสำหรับการออกใบเสร็จ/ใบกำกับภาษี"
                }
              />
            </WrapperTextField>

            <WrapperTextField>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="รายละเอียดที่อยู่"
                placeholder="ระบุรายละเอียดที่อยู่สำหรับการออกใบเสร็จ/ใบกำกับภาษี"
                value={formData.billing?.address || ""}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    billing: { ...prev.billing, address: e.target.value },
                  }))
                }
                error={!checkNotEmpty(formData.billing?.address)}
                helperText={
                  !checkNotEmpty(formData.billing?.address) &&
                  "กรุณากรอกที่อยู่สำหรับการออกใบเสร็จ/ใบกำกับภาษี"
                }
              />
            </WrapperTextField>
          </WrapperInfo>

          {/* Tax Invoice Information */}
          <WrapperInfo>
            <FormControlLabel
              key={`is-company-checkbox`}
              label="สั่งซื้อในนามนิติบุคคล (กรณีต้องการใบกำกับภาษี)"
              sx={{
                width: "100%",
                "& .MuiFormControlLabel-label": {
                  fontSize: font.size.lg,
                  fontWeight: 500,
                },
              }}
              control={
                <Checkbox
                  sx={{
                    p: 1,
                    color: Primary,
                    "&.Mui-checked": {
                      color: Primary,
                    },
                    "& .MuiSvgIcon-root": {
                      fontSize: 28,
                    },
                  }}
                  value={formData.billing?.is_company || false}
                  checked={formData.billing?.is_company || false}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      billing: {
                        ...prev.billing,
                        is_company: e.target.checked,
                      },
                    }))
                  }
                />
              }
            />

            {formData.billing?.is_company && (
              <>
                <Box
                  display="flex"
                  alignItems="center"
                  gap={1}
                  mt={1.5}
                  mb={1.5}
                >
                  <DomainIcon sx={{ color: DarkGrey, fontSize: 20 }} />
                  <SpanItem fontWeight={500}>
                    กรุณากรอกข้อมูลนิติบุคคลให้ครบถ้วนเพื่อออกเอกสาร
                  </SpanItem>
                </Box>

                <WrapperTextField>
                  <TextField
                    fullWidth
                    label="เลขผู้เสียภาษี (13 หลัก)"
                    value={formData.billing?.tax_id || ""}
                    onChange={(e) => {
                      if (!/^\d*$/.test(e.target.value)) return; // Only allow numeric input
                      setFormData((prev) => ({
                        ...prev,
                        billing: {
                          ...prev.billing,
                          tax_id: e.target.value,
                        },
                      }));
                    }}
                    error={
                      (formData.billing?.is_company &&
                        !checkNotEmpty(formData.billing?.tax_id)) ||
                      formData.billing?.tax_id.length > 13
                    }
                    helperText={
                      formData.billing?.is_company &&
                      !checkNotEmpty(formData.billing?.tax_id)
                        ? "กรุณากรอกเลขผู้เสียภาษี"
                        : formData.billing?.tax_id.length > 13
                          ? "เลขผู้เสียภาษีต้องไม่เกิน 13 หลัก"
                          : ""
                    }
                  />
                </WrapperTextField>
              </>
            )}
          </WrapperInfo>
        </Grid>

        {/* Summary */}
        <Grid item xs={12} md={4.5}>
          <WrapperInfo>
            <SpanTitle mb={16}>สรุปรายการสั่งซื้อ</SpanTitle>

            <Stack direction="row" justifyContent="space-between">
              <Typography
                variant="body1"
                fontSize={font.size.lg}
                fontWeight={600}
              >
                {formData?.package?.name}
              </Typography>

              <Typography
                variant="body1"
                fontSize={font.size.lg}
                fontWeight={600}
              >
                {convertIntToCurrency(formData?.package.price, false)}.-
              </Typography>
            </Stack>
            <SpanItem>ได้รับ {formData?.package?.total_token} เครดิต</SpanItem>
            <SpanSubTitle>
              อายุการใช้งาน {formData?.package?.expiry_days} วัน
            </SpanSubTitle>

            <Divider sx={{ my: 2, borderColor: White01 }} />

            <Stack direction="row" justifyContent="space-between" mb={2}>
              <SpanItem>ภาษีมูลค่าเพิ่ม (7%)</SpanItem>

              <SpanItem color={Black} fontWeight={500}>
                รวมในราคาแล้ว
              </SpanItem>
            </Stack>

            <Stack direction="row" justifyContent="space-between" mb={3}>
              <SpanTitle>ยอดชำระสุทธิ</SpanTitle>

              <SpanTitle color={Primary}>
                {convertIntToCurrency(formData?.package?.price, false)}.-
              </SpanTitle>
            </Stack>

            <AcceptTermsWrapper>
              <FormControlLabel
                key={`is-accept-terms-checkbox`}
                label={
                  <SpanItem color={Black}>
                    ฉันยอมรับ <span>เงื่อนไขการให้บริการ</span> และ{" "}
                    <span>นโยบายความเป็นส่วนตัว</span> ของ XSURFACE
                  </SpanItem>
                }
                sx={{
                  width: "100%",
                  mb: 2,
                  "&.MuiFormControlLabel-root": {
                    alignItems: "flex-start",
                    mr: 0,
                  },
                }}
                control={
                  <Checkbox
                    sx={{
                      p: 1,
                      color: Grey,
                      "&.Mui-checked": {
                        color: Primary,
                      },
                      "& .MuiSvgIcon-root": {
                        fontSize: 28,
                      },
                    }}
                    value={isAcceptTerms || false}
                    checked={isAcceptTerms || false}
                    onChange={(e) => setIsAcceptTerms(e.target.checked)}
                  />
                }
              />

              <Button
                variant="text"
                fullWidth
                startIcon={<ArticleIcon />}
                onClick={() =>
                  openDialog({
                    type: "custom",
                    customDialog: (
                      <TermsPolicyDialog
                        onClose={() => closeDialog()}
                        handleAccept={() => {
                          setIsAcceptTerms(true);
                          closeDialog();
                        }}
                      />
                    ),
                  })
                }
              >
                อ่านรายละเอียดเงื่อนไขทั้งหมด
              </Button>
            </AcceptTermsWrapper>

            <Button
              variant="contained"
              size="large"
              fullWidth
              sx={{ mt: 3 }}
              startIcon={<PaymentIcon />}
              disabled={!isAcceptTerms || !isValid}
              onClick={handleSubmit}
            >
              ยืนยันการชำระเงิน
            </Button>
          </WrapperInfo>

          <Box mt={3}>
            <img
              src="/assets/banner-seller-topup.png"
              alt="ad"
              width="100%"
              height="100%"
            />
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
};

export default PaymentSection;
